import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { formatDateAsNumeric } from '@/utils/dateFormatter';
import { ComponentTable } from '../Common/ComponentTable';
import { ComponentTableBody } from '../Common/ComponentTableBody';
import { ComponentTableHeader } from '../Common/ComponentTableHeader';
import { LoadingErrorWrapper } from '../Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '../types';

export const COMPONENT_ID = 'asset_reserves_table';

const ASSET_RESERVES_QUERY = gql`
  query assetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      formattedRemainingReserves
      reserve {
        formattedTotalReservesMmboe
        formattedTotalAccumulatedMmboe
        formattedTotalRemainingReservesPercentage
        formattedTotalRemainingReservesLiquidsMmbbl
        formattedTotalRemainingReservesGasBcf
        createdAt
      }
    }
  }
`;

interface AssetReservesData {
  totalReserves: string | number;
  accumulatedProduction: string | number;
  remainingReservesPercentage: string | number;
  remainingReserves: string;
  remainingLiquids: string;
  remainingGas: string;
  reserveDate: string;
}

const formatAssetReserves = (asset: Asset): AssetReservesData => {
  return {
    totalReserves: asset.reserve?.formattedTotalReservesMmboe || 'N/A',
    accumulatedProduction: asset.reserve?.formattedTotalAccumulatedMmboe || 'N/A',
    remainingReservesPercentage: asset.reserve?.formattedTotalRemainingReservesPercentage || 'N/A',
    remainingReserves: asset.formattedRemainingReserves || 'N/A',
    remainingLiquids: asset.reserve?.formattedTotalRemainingReservesLiquidsMmbbl || 'N/A',
    remainingGas: asset.reserve?.formattedTotalRemainingReservesGasBcf || 'N/A',
    reserveDate: asset.reserve?.createdAt ? formatDateAsNumeric(asset.reserve.createdAt) : 'N/A',
  };
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<AssetReservesData>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const { loading, error } = useQuery<{ assetV2: Asset }>(ASSET_RESERVES_QUERY, {
    variables: { input: { id: assetId } },
    skip: !!persistedData || !assetId,
    onCompleted: response => {
      if (response?.assetV2) {
        const data = formatAssetReserves(response.assetV2);
        setDataFetched(data);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

const ComponentView = ({ data }: ComponentViewProps<AssetReservesData>) => {
  if (!data) return null;

  const rows = [
    { label: 'Total Reserves (MMboe)', value: data.totalReserves },
    { label: 'Accumulated Production (MMbbl)', value: data.accumulatedProduction },
    { label: 'Remaining Reserves (% of Total Reserves)', value: data.remainingReservesPercentage },
    { label: 'Remaining Reserves (MMboe)', value: data.remainingReserves },
    { label: 'Remaining Oil + Condensate (MMbbl)', value: data.remainingLiquids },
    { label: 'Remaining Gas (Bcf)', value: data.remainingGas },
  ];

  return (
    <Box
      display='flex'
      flexDirection='column'
      width='100%'
      height='100%'
      maxWidth='100%'
      maxHeight='100%'
      overflow='auto'
    >
      <ComponentTable>
        <ComponentTableHeader>Reserves - {data.reserveDate}</ComponentTableHeader>
        <ComponentTableBody rows={rows} />
      </ComponentTable>
    </Box>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetReservesData>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <ComponentView data={data} />}
    </LoadingErrorWrapper>
  );
};

export const AssetReservesTableComponent: ComponentDefinition<AssetReservesData> = {
  id: COMPONENT_ID,
  name: 'Asset Reserves',
  type: SmartReportComponentTypesEnum.Table,
  reportTypes: [SmartReportTypeEnum.Asset],
  Component: ComponentEdit,
  ComponentView,
};
